import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../lib/api'
import type { Review, ReviewSolicitation, ReputationStats, SolicitationRule, ReviewPlatform } from '../types/reputation'

const QUERY_KEYS = {
  reviews: ['reputation', 'reviews'] as const,
  solicitations: ['reputation', 'solicitations'] as const,
  stats: ['reputation', 'stats'] as const,
  rules: ['reputation', 'rules'] as const,
}

export function useReviews(filters?: { platform?: string; sentiment?: string; responded?: boolean }) {
  return useQuery({
    queryKey: [...QUERY_KEYS.reviews, filters],
    queryFn: async () => {
      const { data } = await api.get('/reputation/reviews', { params: filters })
      return (data.data ?? data) as Review[]
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function useReviewSolicitations() {
  return useQuery({
    queryKey: QUERY_KEYS.solicitations,
    queryFn: async () => {
      const { data } = await api.get('/reputation/solicitations')
      return data as ReviewSolicitation[]
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function useReputationStats() {
  return useQuery({
    queryKey: QUERY_KEYS.stats,
    queryFn: async () => {
      const { data } = await api.get('/reputation/stats')
      return data as ReputationStats
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function useSolicitationRules() {
  return useQuery({
    queryKey: QUERY_KEYS.rules,
    queryFn: async () => {
      const { data } = await api.get('/reputation/rules')
      return data as SolicitationRule[]
    },
  })
}

export function useRespondToReview() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, content }: { id: string; content: string }) => {
      const { data } = await api.put(`/reputation/reviews/${id}/respond`, { content })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.reviews })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.stats })
    },
  })
}

export function useUpdateSolicitationRule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: Partial<SolicitationRule> }) => {
      const { data } = await api.put(`/reputation/rules/${id}`, payload)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.rules })
    },
  })
}

export function useCreateReviewSolicitation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: {
      patientId: string
      checkInId?: string
      platform: ReviewPlatform
      channel: 'email' | 'sms'
    }) => {
      const { data } = await api.post('/reputation/solicitations', payload)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.solicitations })
    },
  })
}

export { QUERY_KEYS }
